import { useEffect, useMemo, useState, useRef, type ReactNode } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
// Using a native HTML table (styled similar to AdminActivos) instead of the
// `Table` UI primitives to match layout and spacing from the design.
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useLanguage } from "./LanguageContext";
import { db } from "../lib/supabase";
import { usePagination } from "@/hooks/usePagination";
import { TablePagination } from "./ui/table-pagination";

// Clean icon components
const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8"></circle>
    <path d="M21 21l-4.35-4.35"></path>
  </svg>
);

const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
);

export function Portfolio() {
  const [searchTerm, setSearchTerm] = useState("");
  const { getBilingualText } = useLanguage();
  const [rows, setRows] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  // Map DB row to UI holding format used by table
  function normalizeType(value: any) {
    const raw = String(value ?? "Temporal").trim();
    const lower = raw.toLowerCase();
    // Estandarizar a los únicos valores: Temporal, Tradicional, Habitaciones
    if (lower === "-" || lower === "" || lower === "n/a") return "Temporal";
    if (lower.includes("tradicional")) return "Tradicional";
    if (lower.includes("larga")) return "Tradicional"; // agrupar 'Larga Temporada' como Tradicional
    if (lower.includes("habitacion")) return "Habitaciones";
    return "Temporal";
  }

  function mapRow(row: any) {
    const explotacion = normalizeType(row.tipo_explotacion);
    // Map DB 'estado' values to internal status codes used in AdminActivos
    const rawEstado = String(row.estado ?? "alquilado").trim().toLowerCase();
    const estadoMap: Record<string, string> = {
      "alquilado": "CASHFLOWING",
      "alquilándose": "RENTING",
      "alquilandose": "RENTING",
      "en obras": "REFORMA",
      "en reforma": "REFORMA",
      "en venta": "ARRAS",
      "bajo arras": "ARRAS",
    };
    const estado = estadoMap[rawEstado] ?? "CASHFLOWING";
    // Format currency values to €X.XXX
    const fmtEUR = (n: any) => {
      const num = Number(n ?? 0);
      return num === 0
        ? "€0"
        : new Intl.NumberFormat("es-ES").format(num).replace(/^(.*)$/, "€$1");
    };
    // Format dates to DD/MM/YYYY
    const fmtDate = (d: any) => {
      if (!d) return "-";
      const date = new Date(d);
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    };
    // helpers to parse raw numeric/date values for sorting
    const parseNumber = (v: any) => {
      if (v == null) return 0;
      if (typeof v === 'number' && !isNaN(v)) return v;
      let s = String(v).trim();
      // remove currency symbols and spaces
      s = s.replace(/[€$£%\s]/g, '');
      if (s === '') return 0;
      const hasDot = s.indexOf('.') !== -1;
      const hasComma = s.indexOf(',') !== -1;
      // If both present assume dot is thousand separator and comma is decimal (e.g. 1.234,56)
      if (hasDot && hasComma) {
        s = s.replace(/\./g, '');
        s = s.replace(/,/g, '.');
      } else if (hasComma && !hasDot) {
        // e.g. 1234,56 -> 1234.56
        s = s.replace(/,/g, '.');
      } else {
        // keep dots as decimal separator
      }
      // Remove any remaining non-digit/non-dot/non-minus characters
      s = s.replace(/[^0-9.\-]/g, '');
      const n = Number(s);
      return isNaN(n) ? 0 : n;
    };
    const parseDate = (d: any) => {
      if (!d) return null;
      const dt = new Date(d);
      return isNaN(dt.getTime()) ? null : dt.getTime();
    };

    return {
      activo: row.nombre ?? "",
      ciudad: row.ciudad ?? "",
      costeTotal: fmtEUR(row.coste_total),
      valorActual: fmtEUR(row.valor_actual),
      rentasAnual: fmtEUR(row.rentas_anuales),
      hipoteca: fmtEUR(row.hipoteca),
      estado,
      detalles: row.notas ?? null,
      fechaCompra: fmtDate(row.fecha_compra),
      fechaPrimerAlquiler: fmtDate(row.fecha_primer_alquiler),
      vacancy12m: row.vacancy_12m ?? "-",
      explotacion,
      // raw values for sorting
      costeTotalRaw: parseNumber(row.coste_total),
      valorActualRaw: parseNumber(row.valor_actual),
      rentasAnualRaw: parseNumber(row.rentas_anuales),
      hipotecaRaw: parseNumber(row.hipoteca),
      fechaCompraRaw: parseDate(row.fecha_compra),
      fechaPrimerAlquilerRaw: parseDate(row.fecha_primer_alquiler),
      vacancy12mRaw: parseNumber(row.vacancy_12m),
    };
  }

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      try {
        const { data, total } = await db.getActivosPaged(
          searchTerm,
          page,
          pageSize
        );
        const mapped = Array.isArray(data) ? data.map(mapRow) : [];
        setRows(mapped);
        setTotal(total);
      } catch (e) {
        console.error("getActivos (portfolio) error:", e);
      }
    };
    // basic debounce
    const id = setTimeout(load, 250);
    return () => {
      clearTimeout(id);
      controller.abort();
    };
  }, [searchTerm, page, pageSize]);

  const getEstadoBadge = (estado: string) => {
    // Expect internal status codes (CASHFLOWING, RENTING, REFORMA, ARRAS)
    const labelFor = (text: ReactNode) => {
      if (text == null) return "";
      if (typeof text === "string") return text.trim();
      return text;
    };

    switch (estado) {
      case "CASHFLOWING":
        return (
          <Badge
            className="font-medium text-xs px-3 py-1 whitespace-nowrap rounded-full"
            style={{
              backgroundColor: "#E6FFF4",
              color: "#00D26A",
              border: "1px solid #00D26A",
            }}
          >
            {labelFor("CASHFLOWING")}
          </Badge>
        );
      case "REFORMA":
        return (
          <Badge
            className="font-medium text-xs px-3 py-1 whitespace-nowrap rounded-full"
            style={{
              backgroundColor: "#FFF8E6",
              color: "#FF8C00",
              border: "1px solid #FF8C00",
            }}
          >
            {labelFor(getBilingualText("portfolio.status.development"))}
          </Badge>
        );
      case "RENTING":
        return (
          <Badge
            className="font-medium text-xs px-3 py-1 whitespace-nowrap rounded-full"
            style={{
              backgroundColor: "#F3E8FF",
              color: "#8B5CF6",
              border: "1px solid #8B5CF6",
            }}
          >
            {labelFor("RENTING")}
          </Badge>
        );
      case "ARRAS":
        return (
          <Badge
            className="font-medium text-xs px-3 py-1 whitespace-nowrap rounded-full"
            style={{
              backgroundColor: "#FFF8E6",
              color: "#FF8C00",
              border: "1px solid #FF8C00",
            }}
          >
            {labelFor("BAJO ARRAS")}
          </Badge>
        );
      default:
        return <Badge variant="outline" className="text-xs whitespace-nowrap">{labelFor(estado)}</Badge>;
    }
  };

  // Results already filtered server-side by nombre; keep client-side city match as a convenience
  const filteredHoldings = useMemo(
    () =>
      rows.filter(
        (holding) =>
          (holding.activo || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (holding.ciudad || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      ),
    [rows, searchTerm]
  );

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const sortedHoldings = useMemo(() => {
    const list = [...filteredHoldings];
    if (!sortBy) return list;
    list.sort((a: any, b: any) => {
      const dir = sortDir === 'desc' ? -1 : 1;
      switch (sortBy) {
        case 'activo':
          return dir * String(a.activo || '').localeCompare(String(b.activo || ''), undefined, { numeric: true, sensitivity: 'base' });
        case 'ciudad':
          return dir * String(a.ciudad || '').localeCompare(String(b.ciudad || ''), undefined, { numeric: true, sensitivity: 'base' });
        case 'explotacion':
          return dir * String(a.explotacion || '').localeCompare(String(b.explotacion || ''), undefined, { sensitivity: 'base' });
        case 'costeTotal':
          return dir * ((a.costeTotalRaw || 0) - (b.costeTotalRaw || 0));
        case 'valorActual':
          return dir * ((a.valorActualRaw || 0) - (b.valorActualRaw || 0));
        case 'rentasAnual':
          return dir * ((a.rentasAnualRaw || 0) - (b.rentasAnualRaw || 0));
        case 'hipoteca':
          return dir * ((a.hipotecaRaw || 0) - (b.hipotecaRaw || 0));
        case 'fechaCompra':
          return dir * ((a.fechaCompraRaw || 0) - (b.fechaCompraRaw || 0));
        case 'fechaPrimerAlquiler':
          return dir * ((a.fechaPrimerAlquilerRaw || 0) - (b.fechaPrimerAlquilerRaw || 0));
        case 'vacancy12m':
          return dir * ((a.vacancy12mRaw || 0) - (b.vacancy12mRaw || 0));
        case 'estado':
          return dir * String(a.estado || '').localeCompare(String(b.estado || ''), undefined, { sensitivity: 'base' });
        default:
          return 0;
      }
    });
    return list;
  }, [filteredHoldings, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Función para calcular totales
  const calculateTotals = () => {
    // Use the raw numeric fields produced in mapRow to avoid parsing formatted strings
    return filteredHoldings.reduce(
      (acc, holding) => {
        const coste = Number(holding.costeTotalRaw ?? 0) || 0;
        const valor = Number(holding.valorActualRaw ?? 0) || 0;
        const renta = Number(holding.rentasAnualRaw ?? 0) || 0;
        const hipoteca = Number(holding.hipotecaRaw ?? 0) || 0;

        return {
          costeTotal: acc.costeTotal + coste,
          valorActual: acc.valorActual + valor,
          rentasAnual: acc.rentasAnual + renta,
          hipoteca: acc.hipoteca + hipoteca,
        };
      },
      {
        costeTotal: 0,
        valorActual: 0,
        rentasAnual: 0,
        hipoteca: 0,
      }
    );
  };

  const totals = calculateTotals();

  // Refs and sync logic to show scrollbars on top and bottom
  const tableRef = useRef<HTMLTableElement | null>(null);
  const topScrollerRef = useRef<HTMLDivElement | null>(null);
  const topInnerRef = useRef<HTMLDivElement | null>(null);
  const bottomWrapperRef = useRef<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);
  

  useEffect(() => {
    const syncWidths = () => {
      const table = tableRef.current;
      const topInner = topInnerRef.current;
      if (!table || !topInner) return;
      topInner.style.width = `${table.scrollWidth}px`;
      const isMobile = window.innerWidth < 768;
      // Always show the hint on mobile regardless of actual overflow
      setShowScrollHint(isMobile);
    };

    // initial sync
    syncWidths();
    // keep in sync on resize
    window.addEventListener("resize", syncWidths);

    const top = topScrollerRef.current;
    const bottom = bottomWrapperRef.current;
    if (top && bottom) {
      const onTopScroll = () => {
        if (bottom) bottom.scrollLeft = top.scrollLeft;
      };
      const onBottomScroll = () => {
        if (top) top.scrollLeft = bottom.scrollLeft;
      };
      top.addEventListener("scroll", onTopScroll);
      bottom.addEventListener("scroll", onBottomScroll);

      return () => {
        top.removeEventListener("scroll", onTopScroll);
        bottom.removeEventListener("scroll", onBottomScroll);
        window.removeEventListener("resize", syncWidths);
      };
    }

    return () => window.removeEventListener("resize", syncWidths);
  }, [filteredHoldings.length]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl text-[#0F172A] mb-2" style={{ fontWeight: 600 }}>
          {getBilingualText("portfolio.header.title")}
        </h1>
        <p className="text-gray-600">
          {getBilingualText("portfolio.header.subtitle")}
        </p>
      </div>

      {/* Solo Holdings - Capital Account y Activity eliminados */}
      <div className="w-full space-y-6">
        {/* Solo Buscador */}
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-md">
            <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
              <SearchIcon />
            </div>
            <Input
              placeholder={
                typeof getBilingualText("common.search") === "string"
                  ? getBilingualText("common.search") + " activos..."
                  : "Buscar activos..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-10 rounded-full bg-white border border-transparent shadow-sm placeholder-gray-400 focus:outline-none focus:ring-0 focus-visible:shadow-[inset_0_0_0_2px_rgba(226,232,240,1)] active:shadow-[inset_0_0_0_2px_rgba(226,232,240,1)]"
            />
          </div>
        </div>

        {/* Activos Table - HTML table styled like AdminActivos */}
        <Card className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="p-0">
            <div>
              {/* Top scroller - mirrors the table width and shows scrollbar on top */}
              {showScrollHint && (
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-2 md:hidden transition-opacity duration-300 opacity-100">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500" aria-hidden="true">
                    <path d="M3 12h18" />
                    <path d="M7 6L3 12L7 18" />
                    <path d="M17 6L21 12L17 18" />
                  </svg>
                  <span>Desliza horizontalmente</span>
                </div>
              )}
              <div ref={topScrollerRef} className="overflow-x-auto overflow-y-hidden" style={{height: 16}}>
                <div ref={topInnerRef} style={{height: 1}} />
              </div>

              <div ref={bottomWrapperRef} className="overflow-x-auto portfolio-wrapper w-full rounded-lg bg-white">
              <style>{`
                /* Portfolio table responsive styles (mobile: show only Propiedad, Tipo, Estado) */
                .portfolio-table { width: 100%; table-layout: fixed; }
                .portfolio-colgroup col { width: auto; }

                @media (max-width: 767px) {
                  /* Keep table layout fixed but do not hide columns; use horizontal scrolling in wrapper */
                  .portfolio-table { table-layout: fixed; }
                  /* Give primary columns more space on small screens */
                  .portfolio-colgroup col.col-name { width: 55% !important; }
                  .portfolio-colgroup col.col-type { width: 22.5% !important; }
                  .portfolio-colgroup col.col-status { width: 22.5% !important; }

                  /* Slightly reduce horizontal padding on very small screens and keep cells single-line (scroll handles overflow) */
                  .portfolio-table th, .portfolio-table td { padding-left: 0.5rem !important; padding-right: 0.5rem !important; white-space: nowrap; }

                  /* Ensure badges and inline content truncate visually without expanding cells */
                  .portfolio-table td .badge, .portfolio-table td .inline-flex { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; }
                }

                @media (min-width: 768px) {
                  .portfolio-table { table-layout: auto; }
                  .portfolio-colgroup col { width: auto !important; }
                }

                /* Wrapper + scroll behavior: keep table inside viewport and enable internal horizontal scroll */
                .portfolio-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; max-width: 100vw; box-sizing: border-box; }
                .portfolio-table { width: max-content; min-width: 100%; table-layout: auto; }
                /* Force single-line cells so the table grows horizontally and the wrapper shows an internal scrollbar */
                .portfolio-table th, .portfolio-table td { white-space: nowrap; }
                /* Allow wrap on very narrow phones to avoid extreme overflow while keeping data visible */
                @media (max-width: 480px) { .portfolio-table th, .portfolio-table td { white-space: normal; } }

                /* Ensure paddings/widths don't push table outside viewport */
                .portfolio-table, .portfolio-table th, .portfolio-table td { box-sizing: border-box; }

                /* By default remove truncation from table cells so data stays with its column; rely on wrapper scroll to view overflow */
                .portfolio-table .truncate { overflow: visible !important; text-overflow: clip !important; white-space: nowrap !important; display: inline-block !important; }
                /* Apply truncation (ellipsis) only to the 'Estado' column (11th) when .truncate is present */
                .portfolio-table td:nth-child(11).truncate, .portfolio-table th:nth-child(11).truncate {
                  overflow: hidden !important;
                  text-overflow: ellipsis !important;
                  white-space: nowrap !important;
                  max-width: 120px !important;
                }

                /* Sortable header styles: keep titles aligned and place indicator centered below each title */
                .sortable-header { cursor: pointer; transition: background-color .12s ease; position: relative; padding-bottom: 1.25rem; }
                .sortable-header:hover { background-color: #f8fafc; }
                .sortable-header .header-title { display: block; height: 1.25rem; line-height: 1.25rem; overflow: hidden; text-align: center; width: 100%; }
                /* Left-align the Propiedad title specifically */
                .sortable-header.header-property .header-title { text-align: left; padding-left: 0.25rem; }
                .sortable-header .sort-indicator { position: absolute; left: 50%; transform: translateX(-50%); bottom: 6px; color: #9ca3af; font-weight: 600; font-size: 12px; opacity: 0; transition: opacity .12s ease, transform .12s ease; }
                .sortable-header:hover .sort-indicator { opacity: 1; }
                .sortable-header .sort-indicator.active { opacity: 1; color: #111827; }
              `}</style>

              <table ref={tableRef} className="table-auto divide-y divide-gray-200 portfolio-table">
                <colgroup className="portfolio-colgroup">
                  <col className="col-index" style={{ width: '48px' }} />
                  <col className="col-name" style={{ width: '24%' }} />
                  <col className="col-location" style={{ width: '140px' }} />
                  <col className="col-type" />
                  <col className="col-investment" />
                  <col className="col-currentValue" />
                  <col className="col-annualRent" />
                  <col className="col-mortgage" />
                  <col className="col-purchaseDate" />
                  <col className="col-vacancy" />
                  <col className="col-status" />
                  <col className="col-actions" />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200 bg-white">
                    <th className="text-left px-2 py-4 text-xs text-gray-600 uppercase tracking-wider" style={{ fontWeight: 600, width: '48px' }}>#</th>
                    {/* index column hidden */}
                    <th className="text-left px-2 py-4 text-xs text-gray-600 uppercase tracking-wider sortable-header header-property" style={{ fontWeight: 600, width: '24%' }} onClick={() => handleSort('activo')}>
                      <div className="flex flex-col items-start">
                        <span className="header-title">{getBilingualText('portfolio.table.property')}</span>
                        <span className={`sort-indicator ${sortBy==='activo' ? 'active' : ''}`}>{sortBy==='activo' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</span>
                      </div>
                    </th>
                    <th className="text-left px-2 py-4 text-xs text-gray-600 uppercase tracking-wider max-w-[140px] whitespace-normal sortable-header" style={{ fontWeight: 600, width: '14%' }} onClick={() => handleSort('ciudad')}>
                      <div className="flex flex-col items-start">
                        <span className="header-title">{getBilingualText('portfolio.table.location')}</span>
                        <span className={`sort-indicator ${sortBy==='ciudad' ? 'active' : ''}`}>{sortBy==='ciudad' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</span>
                      </div>
                    </th>
                    <th className="text-left px-2 py-4 text-xs text-gray-600 uppercase tracking-wider sortable-header" style={{ fontWeight: 600, width: '10%' }} onClick={() => handleSort('explotacion')}>
                      <div className="flex flex-col items-start">
                        <span className="header-title">{getBilingualText('portfolio.table.type')}</span>
                        <span className={`sort-indicator ${sortBy==='explotacion' ? 'active' : ''}`}>{sortBy==='explotacion' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</span>
                      </div>
                    </th>
                    <th className="text-right px-2 py-4 text-xs text-gray-600 uppercase tracking-wider sortable-header" style={{ fontWeight: 600, width: '9%' }} onClick={() => handleSort('costeTotal')}>
                      <div className="flex flex-col items-start">
                        <span className="header-title">{getBilingualText('portfolio.table.investment')}</span>
                        <span className={`sort-indicator ${sortBy==='costeTotal' ? 'active' : ''}`}>{sortBy==='costeTotal' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</span>
                      </div>
                    </th>
                    <th className="text-right px-2 py-4 text-xs text-gray-600 uppercase tracking-wider sortable-header" style={{ fontWeight: 600, width: '9%' }} onClick={() => handleSort('valorActual')}>
                      <div className="flex flex-col items-start">
                        <span className="header-title">{getBilingualText('portfolio.table.currentValue')}</span>
                        <span className={`sort-indicator ${sortBy==='valorActual' ? 'active' : ''}`}>{sortBy==='valorActual' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</span>
                      </div>
                    </th>
                    <th className="text-right px-2 py-4 text-xs text-gray-600 uppercase tracking-wider sortable-header" style={{ fontWeight: 600, width: '9%' }} onClick={() => handleSort('rentasAnual')}>
                      <div className="flex flex-col items-start">
                        <span className="header-title">{getBilingualText('portfolio.table.annualRent')}</span>
                        <span className={`sort-indicator ${sortBy==='rentasAnual' ? 'active' : ''}`}>{sortBy==='rentasAnual' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</span>
                      </div>
                    </th>
                    <th className="text-right px-2 py-4 text-xs text-gray-600 uppercase tracking-wider sortable-header" style={{ fontWeight: 600, width: '8%' }} onClick={() => handleSort('hipoteca')}>
                      <div className="flex flex-col items-start">
                        <span className="header-title">{getBilingualText('portfolio.table.mortgage')}</span>
                        <span className={`sort-indicator ${sortBy==='hipoteca' ? 'active' : ''}`}>{sortBy==='hipoteca' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</span>
                      </div>
                    </th>
                    <th className="text-center px-2 py-4 text-xs text-gray-600 uppercase tracking-wider sortable-header" style={{ fontWeight: 600, width: '8%' }} onClick={() => handleSort('fechaCompra')}>
                      <div className="flex flex-col items-start">
                        <span className="header-title">{getBilingualText('portfolio.table.purchaseDate')}</span>
                        <span className={`sort-indicator ${sortBy==='fechaCompra' ? 'active' : ''}`}>{sortBy==='fechaCompra' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</span>
                      </div>
                    </th>
                    <th className="text-center px-2 py-4 text-xs text-gray-600 uppercase tracking-wider sortable-header" style={{ fontWeight: 600, width: '7%' }} onClick={() => handleSort('vacancy12m')}>
                      <div className="flex flex-col items-start">
                        <span className="header-title">{getBilingualText('portfolio.table.vacancy12m')}</span>
                        <span className={`sort-indicator ${sortBy==='vacancy12m' ? 'active' : ''}`}>{sortBy==='vacancy12m' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</span>
                      </div>
                    </th>
                    <th className="text-center px-2 py-4 text-xs text-gray-600 uppercase tracking-wider sortable-header" style={{ fontWeight: 600, width: '9%' }} onClick={() => handleSort('estado')}>
                      <div className="flex flex-col items-start">
                        <span className="header-title">{getBilingualText('portfolio.table.status')}</span>
                        <span className={`sort-indicator ${sortBy==='estado' ? 'active' : ''}`}>{sortBy==='estado' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredHoldings.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="py-8 text-center text-sm text-muted-foreground">{getBilingualText('portfolio.empty.noResults') || 'Oh, no parece que no hay nada por aquí'}</td>
                    </tr>
                  ) : (
                    sortedHoldings.map((holding, index) => (
                      <tr key={`${index}-${holding.activo}`} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => {
                        const raw = holding.detalles || 'https://drive.google.com/drive/folders/1VyWRxne0BMYyEsFyBsRIsqddVwysrwau?usp=sharing';
                        const url = (() => {
                          if (!raw) return raw;
                          if (/^https?:\/\//i.test(raw) || raw.startsWith('//')) return raw;
                          if (raw.startsWith('/')) return raw; // absolute path on same origin
                          if (raw.includes('.')) return `https://${raw}`; // looks like a domain
                          return raw;
                        })();
                        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
                        if (newWindow) newWindow.opener = null;
                      }}>
                        <td className="px-2 py-4 text-sm text-gray-600">{(page - 1) * pageSize + index + 1}</td>
                        <td className="px-2 py-4 font-medium truncate max-w-[220px]">{holding.activo}</td>
                        <td title={holding.ciudad} className="px-2 py-4 max-w-[140px] break-words whitespace-normal">{holding.ciudad}</td>
                        <td className="px-2 py-4">
                          {holding.explotacion === 'Habitaciones' ? (
                            <span className="inline-flex items-center text-xs font-semibold rounded-full bg-black text-white px-3 py-1">
                              {holding.explotacion}
                            </span>
                          ) : (
                            <Badge variant="outline" className="text-xs">{holding.explotacion}</Badge>
                          )}
                        </td>
                        <td className="px-2 py-4 text-right">{holding.costeTotal}</td>
                        <td className="px-2 py-4 text-right">{holding.valorActual}</td>
                        <td className="px-2 py-4 text-right">{holding.rentasAnual}</td>
                        <td className="px-2 py-4 text-right">{holding.hipoteca}</td>
                          <td className="px-2 py-4 text-center text-sm">{holding.fechaCompra}</td>
                        <td className="px-2 py-4 text-center">
                          <span className={`text-xs font-medium ${
                            holding.vacancy12m === '0.0%' ? 'text-green-600' :
                            holding.vacancy12m === '17.0%' ? 'text-orange-800' :
                            holding.vacancy12m === '100%' ? 'text-red-600' :
                            holding.vacancy12m === '-' ? 'text-gray-400' :
                            (parseFloat(String(holding.vacancy12m).replace('%','')) > 10) ? 'text-orange-600' : 'text-yellow-600'
                          }`}>{holding.vacancy12m}</span>
                        </td>
                        <td className="px-2 py-4 text-left">
                          <div className="flex items-center gap-2">
                            <div>{getEstadoBadge(holding.estado)}</div>
                            <div className="block">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-sm bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100 hover:border-blue-300"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const raw = holding.detalles || 'https://drive.google.com/drive/folders/1VyWRxne0BMYyEsFyBsRIsqddVwysrwau?usp=sharing';
                                  const url = (() => {
                                    if (!raw) return raw;
                                    if (/^https?:\/\//i.test(raw) || raw.startsWith('//')) return raw;
                                    if (raw.startsWith('/')) return raw;
                                    if (raw.includes('.')) return `https://${raw}`;
                                    return raw;
                                  })();
                                  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
                                  if (newWindow) newWindow.opener = null;
                                }}
                              >
                                {getBilingualText('portfolio.actions.viewDetail')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}

                    <tr className="bg-gray-50/50 border-t-2 border-gray-300">
                      <td className="px-2 py-4 font-semibold">{filteredHoldings.length} {getBilingualText('portfolio.totals.assets')}</td>
                      <td className="px-2 py-4">-</td>
                      <td className="px-2 py-4">-</td>
                      <td className="px-2 py-4">-</td>
                      <td className="px-2 py-4 text-right font-semibold">€{new Intl.NumberFormat('es-ES').format(totals.costeTotal)}</td>
                      <td className="px-2 py-4 text-right font-semibold">€{new Intl.NumberFormat('es-ES').format(totals.valorActual)}</td>
                      <td className="px-2 py-4 text-right font-semibold">€{new Intl.NumberFormat('es-ES').format(totals.rentasAnual)}</td>
                      <td className="px-2 py-4 text-right font-semibold">€{new Intl.NumberFormat('es-ES').format(totals.hipoteca)}</td>
                      <td className="px-2 py-4">-</td>
                      <td className="px-2 py-4">-</td>
                      <td className="px-2 py-4">-</td>
                      <td className="px-2 py-4">-</td>
                    </tr>
                </tbody>
              </table>
              </div>
            </div>
          </CardContent>
        </Card>
        <TablePagination
          page={page}
          totalPages={totalPages}
          pageSize={pageSize}
          onPrev={() => setPage(Math.max(1, page - 1))}
          onNext={() => setPage(Math.min(totalPages, page + 1))}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
}
